/**
 * Shared helpers for reading a Control's `implementation_status`/`current_maturity` fields the
 * same way everywhere. Both were previously reimplemented ad hoc on `ControlDetail.tsx` and
 * `StatementOfApplicabilityPage.tsx` with a plain `includes('complet')` / `charAt(0)` parse -
 * confirmed live against the real data (173 ISO controls) that neither ever matches: the only
 * implementation_status values that appear are 'Implemented'/'not_implemented'/'Not Applicable'
 * (never 'Completed'), and current_maturity is a bare label ('Managed', 'Unassessed', ...) with
 * no leading digit for 172 of 173 controls. That made every already-implemented control read as
 * "not fully implemented", and the one control that actually had a real maturity rating
 * ("Managed") read as "not yet assessed".
 */

// The catalog's actual current_maturity/target_maturity options are text labels
// (Initial/Repeatable/Defined/Managed/Optimized) with no leading digit, so a plain
// "first character is a number" parse always failed. Map the real labels to their
// CMMI-style 1-5 score directly, with the digit-prefix parse kept as a fallback for
// any legacy/custom option text that does include one.
export const MATURITY_LABEL_SCORE: Record<string, number> = {
  initial: 1,
  repeatable: 2,
  defined: 3,
  managed: 4,
  optimized: 5,
  optimised: 5,
  unassessed: 0,
};

export const maturityToScore = (raw: unknown): number | null => {
  const text = String(raw ?? '').trim();
  if (!text) return null;
  const digitMatch = text.match(/^(\d)/);
  if (digitMatch) {
    const n = parseInt(digitMatch[1], 10);
    if (n >= 0 && n <= 5) return n;
  }
  const byLabel = MATURITY_LABEL_SCORE[text.toLowerCase()];
  return byLabel !== undefined ? byLabel : null;
};

/**
 * True when a control's implementation_status counts as fully implemented. A status starting
 * with "not" (`not_implemented`, `not applicable`, blank) is never complete; otherwise a status
 * mentioning "implement" or "complet" (Implemented/Completed/Complete) counts. Callers that need
 * to treat "Not Applicable" as excluded rather than as a failure should check for that
 * explicitly first - this only answers "is it complete", not "does this control even apply".
 */
export const isImplementationComplete = (raw: unknown): boolean => {
  const status = String(raw ?? '').toLowerCase().trim();
  if (!status || status.startsWith('not')) return false;
  return /complet|implement/.test(status);
};

/**
 * Parses a stored control_effectiveness string ("72%" or "72% (2/3 factors assessed)") into its
 * percentage. Unanchored to parens - confirmed live that a regex requiring "(NN%)" never matches
 * either real stored format (the % is never inside the parens), which made every effectiveness
 * chart in ReportsPage.tsx silently drop or miscategorise every real control. Returns null (not
 * 0) when there is no percentage to find at all, so a caller can tell "genuinely scored 0%" apart
 * from "not assessed yet" - callers that want the old always-a-number behaviour (e.g. a risk
 * reduction calculation that should just treat "not assessed" as "no reduction") should do
 * `parseEffectivenessScore(raw) ?? 0` themselves rather than have that baked in here.
 */
export const parseEffectivenessScore = (raw: unknown): number | null => {
  if (typeof raw !== 'string' || !raw) return null;
  const match = raw.match(/(\d+)%/);
  if (!match) return null;
  const pct = parseInt(match[1], 10);
  return Number.isFinite(pct) ? pct : null;
};
